// src/components/payroll/PayrollDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { db } from '../../firebase/config';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useCurrency } from '../../utils/currencyUtils';
import { 
  DollarSign, Calendar, Users, Clock, AlertCircle, ChevronRight,
  ArrowUpRight, ArrowDownRight, PlusCircle, FileText
} from 'lucide-react';

const PayrollDashboard = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    nextPayDate: null,
    monthlyPayroll: 0,
    employeeCount: 0,
    pendingPayrun: null,
    recentPayruns: []
  });
  
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch employees count
        const employeesQuery = query(
          collection(db, 'employeePayrollDetails'),
          where('businessId', '==', activeBusiness.id)
        );
        const employeesSnapshot = await getDocs(employeesQuery);
        const employeeCount = employeesSnapshot.size;
        
        // Calculate monthly payroll amount
        let totalMonthlyPayroll = 0;
        employeesSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.salaryType === 'hourly') {
            // Estimate based on 40hrs/week, 4 weeks/month
            totalMonthlyPayroll += (data.baseSalary * 40 * 4);
          } else if (data.salaryType === 'annual') {
            totalMonthlyPayroll += (data.baseSalary / 12);
          } else {
            // Monthly salary
            totalMonthlyPayroll += data.baseSalary;
          }
        });
        
        // Fetch upcoming/pending pay runs
        const pendingRunsQuery = query(
          collection(db, 'payRuns'),
          where('businessId', '==', activeBusiness.id),
          where('status', 'in', ['draft', 'processing']),
          orderBy('paymentDate'),
          limit(1)
        );
        const pendingRunsSnapshot = await getDocs(pendingRunsQuery);
        const pendingPayrun = !pendingRunsSnapshot.empty ? {
          id: pendingRunsSnapshot.docs[0].id,
          ...pendingRunsSnapshot.docs[0].data(),
          paymentDate: pendingRunsSnapshot.docs[0].data().paymentDate?.toDate() || null
        } : null;
        
        // Fetch recent pay runs
        const recentRunsQuery = query(
          collection(db, 'payRuns'),
          where('businessId', '==', activeBusiness.id),
          where('status', '==', 'completed'),
          orderBy('paymentDate', 'desc'),
          limit(3)
        );
        const recentRunsSnapshot = await getDocs(recentRunsQuery);
        const recentPayruns = [];
        recentRunsSnapshot.forEach((doc) => {
          const data = doc.data();
          recentPayruns.push({
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate() || null
          });
        });
        
        setStats({
          nextPayDate: pendingPayrun?.paymentDate || null,
          monthlyPayroll: totalMonthlyPayroll,
          employeeCount,
          pendingPayrun,
          recentPayruns
        });
        
      } catch (err) {
        console.error('Error fetching payroll data:', err);
        setError('Failed to load payroll data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayrollData();
  }, [currentUser, activeBusiness]);
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 text-center">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5]"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Monthly Payroll Cost */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Monthly Payroll</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatAmount(stats.monthlyPayroll)}</h3>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg text-blue-700">
              <DollarSign size={20} />
            </div>
          </div>
          <div className="mt-1 text-sm">
            <span className="text-gray-500">Based on {stats.employeeCount} employees</span>
          </div>
        </div>
        
        {/* Next Pay Date */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Next Pay Date</p>
              <h3 className="text-2xl font-bold text-gray-900">
                {stats.nextPayDate 
                  ? stats.nextPayDate.toLocaleDateString()
                  : 'No scheduled payments'}
              </h3>
            </div>
            <div className="p-2 bg-green-100 rounded-lg text-green-700">
              <Calendar size={20} />
            </div>
          </div>
          {stats.nextPayDate && (
            <div className="mt-1 text-sm">
              <span className="text-gray-500">
                {Math.ceil((stats.nextPayDate - new Date()) / (1000 * 60 * 60 * 24))} days from now
              </span>
            </div>
          )}
        </div>
        
        {/* Active Employees */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Active Employees</p>
              <h3 className="text-2xl font-bold text-gray-900">{stats.employeeCount}</h3>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg text-purple-700">
              <Users size={20} />
            </div>
          </div>
          <div className="mt-1 text-sm">
            <Link to="/payroll/employees" className="text-[#190192] hover:underline flex items-center">
              Manage employees <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
        </div>
        
        {/* Pending Approval */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Time Entries</p>
              <h3 className="text-2xl font-bold text-gray-900">12</h3> {/* This would be dynamic in a real implementation */}
            </div>
            <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
              <Clock size={20} />
            </div>
          </div>
          <div className="mt-1 text-sm">
            <Link to="/payroll/timesheets" className="text-[#190192] hover:underline flex items-center">
              5 pending approval <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Pending Pay Run */}
      {stats.pendingPayrun && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Pending Pay Run</h3>
            <Link 
              to={`/payroll/runs/${stats.pendingPayrun.id}`} 
              className="text-[#190192] hover:underline text-sm flex items-center"
            >
              Manage <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <div className="p-2 bg-amber-100 rounded-full text-amber-700 mr-3">
                  <Calendar size={18} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{stats.pendingPayrun.name}</h4>
                  <p className="text-gray-600 text-sm">
                    Payment date: {stats.pendingPayrun.paymentDate?.toLocaleDateString()}
                  </p>
                  <div className="mt-2 flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      stats.pendingPayrun.status === 'draft' 
                        ? 'bg-gray-100 text-gray-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {stats.pendingPayrun.status === 'draft' ? 'Draft' : 'Processing'}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      {stats.pendingPayrun.employeeCount} employees
                    </span>
                    <span className="ml-2 text-sm font-medium">
                      {formatAmount(stats.pendingPayrun.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex justify-end space-x-2">
                <Link
                  to={`/payroll/runs/${stats.pendingPayrun.id}`}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50"
                >
                  View Details
                </Link>
                <Link
                  to={`/payroll/runs/${stats.pendingPayrun.id}/payslips`}
                  className="px-3 py-1.5 bg-[#190192] text-white rounded-lg text-sm hover:bg-[#2C0DB5]"
                >
                  Manage Payslips
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Recent Pay Runs and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Pay Runs */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Recent Pay Runs</h3>
            <Link 
              to="/payroll/runs" 
              className="text-[#190192] hover:underline text-sm flex items-center"
            >
              View all <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
          <div className="p-6">
            {stats.recentPayruns.length === 0 ? (
              <div className="text-center py-6">
                <Calendar size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No completed pay runs found</p>
                <Link
                  to="/payroll/runs/new"
                  className="mt-2 inline-flex items-center text-[#190192] hover:underline"
                >
                  <PlusCircle size={16} className="mr-1" />
                  Create your first pay run
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {stats.recentPayruns.map((payrun) => (
                  <Link 
                    key={payrun.id}
                    to={`/payroll/runs/${payrun.id}`}
                    className="py-3 px-2 flex items-center justify-between hover:bg-gray-50 rounded-lg -mx-2"
                  >
                    <div className="flex items-center">
                      <div className="mr-3 p-2 bg-green-100 text-green-700 rounded-lg">
                        <DollarSign size={18} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{payrun.name}</p>
                        <p className="text-sm text-gray-500">
                          Paid on {payrun.paymentDate?.toLocaleDateString()} • {payrun.employeeCount} employees
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">{formatAmount(payrun.totalAmount)}</p>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                        Completed
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <Link
                to="/payroll/runs/new"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="p-2 bg-purple-100 rounded-lg text-purple-700 mr-3">
                  <PlusCircle size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Create New Pay Run</p>
                  <p className="text-sm text-gray-500">Start a new payroll process</p>
                </div>
              </Link>
              
              <Link
                to="/payroll/employees/new"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="p-2 bg-blue-100 rounded-lg text-blue-700 mr-3">
                  <Users size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Add Employee</p>
                  <p className="text-sm text-gray-500">Set up payroll for a new employee</p>
                </div>
              </Link>
              
              <Link
                to="/payroll/payslips"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="p-2 bg-green-100 rounded-lg text-green-700 mr-3">
                  <FileText size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Generate Payslips</p>
                  <p className="text-sm text-gray-500">Create and send employee payslips</p>
                </div>
              </Link>
              
              <Link
                to="/payroll/timesheets"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="p-2 bg-amber-100 rounded-lg text-amber-700 mr-3">
                  <Clock size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Review Timesheets</p>
                  <p className="text-sm text-gray-500">Approve employee time entries</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollDashboard;
